import { useIsMobile, useIsTablet } from "./use-breakpoint";

export type CardSize = "sm" | "md" | "lg";

interface ResponsiveCardStyle {
  width: number | string;
  padding: string;
}

// Define all size configurations in one place
const CARD_SIZES = {
  mobile: {
    sm: { width: "calc(100vw - 2rem)", padding: "1rem" },
    md: { width: "calc(100vw - 2rem)", padding: "1rem" },
    lg: { width: "calc(100vw - 2rem)", padding: "1rem" },
  },
  tablet: {
    sm: { width: 440, padding: "1.25rem" },
    md: { width: 520, padding: "1.5rem" },
    lg: { width: 600, padding: "1.5rem" },
  },
  desktop: {
    sm: { width: 520, padding: "1.25rem" },
    md: { width: 640, padding: "1.5rem" },
    lg: { width: 760, padding: "1.75rem" },
  },
} as const;

// Alternative configuration for components that need different sizes
const CARD_SIZES_WIDE = {
  mobile: {
    sm: { width: "calc(100vw - 2rem)", padding: "1rem" },
    md: { width: "calc(100vw - 2rem)", padding: "1rem" },
    lg: { width: "calc(100vw - 2rem)", padding: "1rem" },
  },
  tablet: {
    sm: { width: 480, padding: "1.25rem" },
    md: { width: 560, padding: "1.5rem" },
    lg: { width: 640, padding: "1.5rem" },
  },
  desktop: {
    sm: { width: 560, padding: "1.25rem" },
    md: { width: 680, padding: "1.5rem" },
    lg: { width: 780, padding: "1.75rem" },
  },
} as const;

/**
 * Hook to get responsive card styles based on device type and card size
 * @param size - Card size (sm, md, lg)
 * @param variant - Size variant ('default' or 'wide')
 * @returns Responsive style object with width and padding
 */
export function useResponsiveCardStyle(
  size: CardSize = "md",
  variant: "default" | "wide" = "default"
): ResponsiveCardStyle {
  const isMobile = useIsMobile(); // < 768px (< md)
  const isTablet = useIsTablet(); // 768px - 1023px (md to lg)

  const sizeConfig = variant === "wide" ? CARD_SIZES_WIDE : CARD_SIZES;

  if (isMobile) {
    return sizeConfig.mobile[size];
  }

  if (isTablet) {
    return sizeConfig.tablet[size];
  }

  return sizeConfig.desktop[size];
}

